// kit.ts — Tactical OLED Arcade component kit (style-bible v2). Reusable Phaser primitives that
// encode the handoff's redline anatomy (buttons, chips, toggle, quota pips, progress bars, type
// helpers) so every scene reads as one designed system. Pure GameObjects + PALETTE/FONT + icons;
// honors scaledFont() (85–150% text scale) and motionAllowed(). No Core/network/determinism touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from '../keys';
import { audio } from '../audio';
import { scaledFont, motionAllowed } from '../settings';
import { paintButtonBg, shade } from '../theme';
import { icon, drawIcon, type IconName } from '../icons';

type Sfx = 'uiTap' | 'uiConfirm' | null;

/** Set tracked letter-spacing if the Phaser build supports it (3.60+). */
function track(txt: Phaser.GameObjects.Text, px: number): Phaser.GameObjects.Text {
  const t = txt as unknown as { setLetterSpacing?: (n: number) => Phaser.GameObjects.Text };
  if (typeof t.setLetterSpacing === 'function') t.setLetterSpacing(px);
  return txt;
}

// ─────────────────────────────── Typography ───────────────────────────────

/** Uppercase, tracked section label (display 600, color dim by default). */
export function labelText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  str: string,
  opts: { size?: number; color?: number; origin?: number } = {},
): Phaser.GameObjects.Text {
  const size = scaledFont(opts.size ?? 13, 11);
  const t = scene.add
    .text(x, y, str.toUpperCase(), { fontFamily: FONT.display, fontSize: `${size}px`, color: hex(opts.color ?? PALETTE.dim), fontStyle: '600' })
    .setOrigin(opts.origin ?? 0, 0.5);
  return track(t, size * 0.14);
}

/** Heading (display 600/700). */
export function headingText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  str: string,
  opts: { size?: number; color?: number; origin?: number; weight?: string } = {},
): Phaser.GameObjects.Text {
  const size = scaledFont(opts.size ?? 22, 13);
  return scene.add
    .text(x, y, str, { fontFamily: FONT.display, fontSize: `${size}px`, color: hex(opts.color ?? PALETTE.text), fontStyle: opts.weight ?? 'bold' })
    .setOrigin(opts.origin ?? 0.5, 0.5);
}

// ──────────────────────────────── Buttons ─────────────────────────────────

export interface CtaOpts {
  width?: number;
  height?: number;
  icon?: IconName;
  fill?: number;
  ink?: number;
  glow?: number | null; // glow color; null = none. defaults to fill.
  sfx?: Sfx;
  fontPx?: number;
  upper?: boolean;
}

/** Primary CTA — fill (default `correct`), ink label, optional left icon + glow halo. Redline h≈CTA. */
export function ctaButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  opts: CtaOpts = {},
): Phaser.GameObjects.Container {
  const W = scene.scale.width;
  const w = opts.width ?? Math.min(W * 0.86, 470);
  const h = opts.height ?? 64;
  const fill = opts.fill ?? PALETTE.correct;
  const ink = opts.ink ?? PALETTE.ink;
  const glowColor = opts.glow === null ? null : (opts.glow ?? fill);
  const c = scene.add.container(x, y);

  if (glowColor !== null) {
    // A TIGHT glow — the redline CTA is a flat fill with a contained halo, not a big bleeding bloom.
    const glow = scene.add.graphics();
    glow.lineStyle(3, glowColor, 0.16);
    glow.strokeRoundedRect(-w / 2 - 5, -h / 2 - 5, w + 10, h + 10, Math.min(h * 0.4, 14) + 4);
    c.add(glow);
  }
  const g = scene.add.graphics();
  paintButtonBg(g, w, h, fill, { glossy: false });
  c.add(g);

  const labelStr = opts.upper === false ? label : label.toUpperCase();
  const fontPx = scaledFont(opts.fontPx ?? 21, 13);
  const txt = scene.add
    .text(0, 0, labelStr, { fontFamily: FONT.display, fontSize: `${fontPx}px`, color: hex(ink), fontStyle: 'bold' })
    .setOrigin(0.5);
  track(txt, fontPx * 0.04);

  // layout [icon][gap][text] centered
  const iconSize = Math.round(fontPx * 1.15);
  const gap = opts.icon ? iconSize * 0.45 : 0;
  const tw = txt.width;
  const groupW = (opts.icon ? iconSize + gap : 0) + tw;
  txt.setX((opts.icon ? iconSize + gap : 0) - groupW / 2 + tw / 2);
  c.add(txt);
  let ic: Phaser.GameObjects.Graphics | null = null;
  if (opts.icon) {
    ic = icon(scene, opts.icon, -groupW / 2 + iconSize / 2, 0, iconSize, ink);
    c.add(ic);
  }

  const hit = scene.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add(hit);
  hit.on('pointerover', () => paintButtonBg(g, w, h, shade(fill, 0.08), { glossy: false }));
  hit.on('pointerout', () => {
    paintButtonBg(g, w, h, fill, { glossy: false });
    txt.setY(0); // restore the press-sink if the pointer drags off before release
    ic?.setY(0);
  });
  hit.on('pointerdown', () => {
    paintButtonBg(g, w, h, fill, { pressed: true, glossy: false });
    txt.setY(1);
    ic?.setY(1);
  });
  hit.on('pointerup', () => {
    paintButtonBg(g, w, h, fill, { glossy: false });
    txt.setY(0);
    ic?.setY(0);
    if (opts.sfx !== null) audio().playSfx(opts.sfx ?? 'uiConfirm');
    onClick();
  });
  return c;
}

/** Secondary button — surface fill, text-color label, subtle border (no glow). */
export function secondaryButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  opts: { width?: number; height?: number; icon?: IconName; textColor?: number; fontPx?: number; sfx?: Sfx } = {},
): Phaser.GameObjects.Container {
  const W = scene.scale.width;
  const w = opts.width ?? Math.min(W * 0.86, 470);
  const h = opts.height ?? 50;
  const tint = opts.textColor ?? PALETTE.text;
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  const paint = (hover: boolean): void => {
    g.clear();
    g.fillStyle(hover ? PALETTE.tileHot : PALETTE.surface, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    g.lineStyle(1.25, PALETTE.lineStrong, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
  };
  paint(false);
  c.add(g);
  const fontPx = scaledFont(opts.fontPx ?? 18, 12);
  const txt = scene.add
    .text(0, 0, label.toUpperCase(), { fontFamily: FONT.display, fontSize: `${fontPx}px`, color: hex(tint), fontStyle: '600' })
    .setOrigin(0.5);
  track(txt, fontPx * 0.05);
  const iconSize = Math.round(fontPx * 1.1);
  const gap = opts.icon ? iconSize * 0.45 : 0;
  const groupW = (opts.icon ? iconSize + gap : 0) + txt.width;
  txt.setX((opts.icon ? iconSize + gap : 0) - groupW / 2 + txt.width / 2);
  c.add(txt);
  if (opts.icon) c.add(icon(scene, opts.icon, -groupW / 2 + iconSize / 2, 0, iconSize, tint));
  const hit = scene.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add(hit);
  hit.on('pointerover', () => paint(true));
  hit.on('pointerout', () => paint(false));
  hit.on('pointerup', () => {
    if (opts.sfx !== null) audio().playSfx(opts.sfx ?? 'uiTap');
    onClick();
  });
  return c;
}

/** Square icon button (top-right clusters: help / settings / etc.). */
export function iconButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  name: IconName,
  onClick: () => void,
  opts: { size?: number; color?: number; sfx?: Sfx } = {},
): Phaser.GameObjects.Container {
  const s = opts.size ?? 40;
  const color = opts.color ?? PALETTE.dim;
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  const paint = (hover: boolean): void => {
    g.clear();
    g.fillStyle(hover ? PALETTE.tileHot : PALETTE.surface, 1);
    g.fillRoundedRect(-s / 2, -s / 2, s, s, 9);
    g.lineStyle(1, PALETTE.line, 1);
    g.strokeRoundedRect(-s / 2, -s / 2, s, s, 9);
  };
  paint(false);
  const gl = icon(scene, name, 0, 0, s * 0.5, color);
  const hit = scene.add.rectangle(0, 0, Math.max(s, 44), Math.max(s, 44), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([g, gl, hit]);
  hit.on('pointerover', () => {
    paint(true);
    gl.clear();
    drawIcon(gl, name, s * 0.5, PALETTE.text);
  });
  hit.on('pointerout', () => {
    paint(false);
    gl.clear();
    drawIcon(gl, name, s * 0.5, color);
  });
  hit.on('pointerup', () => {
    if (opts.sfx !== null) audio().playSfx(opts.sfx ?? 'uiTap');
    onClick();
  });
  return c;
}

// ───────────────────────────── Chips / pills ──────────────────────────────

/** Rounded pill chip with optional left icon + label (rank chip, daily chip, rival pill, status). */
export function chip(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  opts: {
    icon?: IconName;
    textColor?: number;
    borderColor?: number;
    fill?: number;
    fontPx?: number;
    upper?: boolean;
    originX?: number;
    onClick?: () => void;
    padX?: number;
    height?: number;
  } = {},
): Phaser.GameObjects.Container {
  const tint = opts.textColor ?? PALETTE.text;
  const fontPx = scaledFont(opts.fontPx ?? 13, 11);
  const padX = opts.padX ?? 12;
  const h = opts.height ?? Math.round(fontPx * 1.9);
  const c = scene.add.container(x, y);
  const txt = scene.add
    .text(0, 0, opts.upper === false ? label : label.toUpperCase(), { fontFamily: FONT.display, fontSize: `${fontPx}px`, color: hex(tint), fontStyle: '600' })
    .setOrigin(0, 0.5);
  track(txt, fontPx * 0.06);
  const iconSize = opts.icon ? Math.round(fontPx * 1.15) : 0;
  const gap = opts.icon ? 6 : 0;
  const w = padX * 2 + iconSize + gap + txt.width;
  const g = scene.add.graphics();
  g.fillStyle(opts.fill ?? PALETTE.surface, opts.fill === undefined ? 0.0 : 1);
  if (opts.fill !== undefined) g.fillRoundedRect(0, -h / 2, w, h, h / 2);
  g.lineStyle(1, opts.borderColor ?? PALETTE.lineStrong, 1);
  g.strokeRoundedRect(0, -h / 2, w, h, h / 2);
  let cx = padX;
  c.add(g);
  if (opts.icon) {
    c.add(icon(scene, opts.icon, cx + iconSize / 2, 0, iconSize, opts.textColor ?? tint));
    cx += iconSize + gap;
  }
  txt.setX(cx);
  c.add(txt);
  // origin handling: shift container so originX maps across width
  const ox = opts.originX ?? 0;
  c.setX(x - w * ox);
  if (opts.onClick) {
    const hit = scene.add.rectangle(w / 2, 0, w, Math.max(h, 36), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    c.add(hit);
    hit.on('pointerup', () => {
      audio().playSfx('uiTap');
      opts.onClick?.();
    });
  }
  c.setData('w', w);
  return c;
}

// ───────────────────────────────── Toggle ─────────────────────────────────

/** 46×26 pill toggle. Returns the container; call setData('on') reads state; onChange fires on tap. */
export function toggle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  initial: boolean,
  onChange: (on: boolean) => void,
): Phaser.GameObjects.Container {
  const w = 46;
  const h = 26;
  const c = scene.add.container(x, y);
  let on = initial;
  const track = scene.add.graphics();
  const knob = scene.add.circle(0, 0, 10, PALETTE.white);
  const paint = (): void => {
    track.clear();
    track.fillStyle(on ? PALETTE.correct : PALETTE.tile, 1);
    track.fillRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    track.lineStyle(1, on ? PALETTE.correct : PALETTE.lineStrong, 1);
    track.strokeRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    knob.setFillStyle(on ? PALETTE.white : PALETTE.mute);
  };
  const place = (animate: boolean): void => {
    const tx = on ? w / 2 - 13 : -w / 2 + 13;
    if (animate && motionAllowed()) scene.tweens.add({ targets: knob, x: tx, duration: 140, ease: 'Cubic.easeOut' });
    else knob.setX(tx);
  };
  paint();
  place(false);
  const hit = scene.add.rectangle(0, 0, Math.max(w, 56), Math.max(h, 44), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([track, knob, hit]);
  hit.on('pointerup', () => {
    on = !on;
    paint();
    place(true);
    audio().playToggle(on); // #61: the two-tone settings toggle (pitched ON/OFF), was a generic uiTap
    onChange(on);
  });
  c.setData('on', () => on);
  // S9 FIX-C: external visual sync (no sfx, no onChange re-entry) — lets a panel keep a toggle honest
  // when a SIBLING control changes the same underlying setting (e.g. dragging Master volume un-mutes
  // Sound, so the Sound toggle must flip ON rather than lie OFF next to a live 90% slider).
  c.setData('set', (v: boolean): void => {
    if (on === v) return;
    on = v;
    paint();
    place(true);
  });
  return c;
}

// ───────────────────────── Quota pips / progress ──────────────────────────

/** A row of quota pips (filled = eaten). Returns the container; pips are children for live updates. */
export function quotaPips(
  scene: Phaser.Scene,
  x: number,
  y: number,
  total: number,
  filled: number,
  opts: { pipW?: number; pipH?: number; gap?: number; fill?: number } = {},
): Phaser.GameObjects.Container {
  const pw = opts.pipW ?? 14;
  const ph = opts.pipH ?? 7;
  const gap = opts.gap ?? 4;
  const c = scene.add.container(x, y);
  for (let i = 0; i < total; i++) {
    const px = i * (pw + gap);
    const on = i < filled;
    const g = scene.add.graphics();
    g.fillStyle(on ? (opts.fill ?? PALETTE.correct) : PALETTE.tile, 1);
    g.fillRoundedRect(px, -ph / 2, pw, ph, 2);
    if (!on) {
      g.lineStyle(1, PALETTE.lineStrong, 1);
      g.strokeRoundedRect(px, -ph / 2, pw, ph, 2);
    }
    c.add(g);
  }
  return c;
}

/** Horizontal progress/XP/timer bar. `frac` 0..1. Returns {container, set(frac, color?)}. */
export function progressBar(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  frac: number,
  color: number = PALETTE.player,
): { container: Phaser.GameObjects.Container; set: (frac: number, color?: number) => void } {
  const c = scene.add.container(x, y);
  const trackG = scene.add.graphics();
  trackG.fillStyle(PALETTE.tile, 1);
  trackG.fillRoundedRect(0, -h / 2, w, h, h / 2);
  const fillG = scene.add.graphics();
  c.add([trackG, fillG]);
  const set = (f: number, col?: number): void => {
    const cl = Math.max(0, Math.min(1, f));
    fillG.clear();
    fillG.fillStyle(col ?? color, 1);
    if (cl > 0) fillG.fillRoundedRect(0, -h / 2, Math.max(h, w * cl), h, h / 2);
  };
  set(frac, color);
  return { container: c, set };
}

// ────────────────────────────── Card token ────────────────────────────────

export interface CardOpts {
  fill?: number; // body fill (default surface2)
  border?: number; // hairline border (default line)
  radius?: number; // corner radius (default 14)
  borderWeight?: number; // stroke weight (default 1.25)
  /** Left accent-rail color; null = none (the rail is the card family's shared signature). */
  accent?: number | null;
}

/**
 * The ONE card token (S3 UI-SYSTEM). A rounded surface with a hairline border and an optional left
 * accent-rail, drawn centered at (x,y) with size w×h. The Result family (RANK-UP banner, tip card,
 * NEXT UNLOCK chip) all converge onto this so border weight / radius / rail read as one system;
 * callers add their own content into the returned container. Pure GameObjects; no state color.
 */
export function cardPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: CardOpts = {},
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const r = opts.radius ?? 14;
  const g = scene.add.graphics();
  g.fillStyle(opts.fill ?? PALETTE.surface2, 1);
  g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
  g.lineStyle(opts.borderWeight ?? 1.25, opts.border ?? PALETTE.line, 1);
  g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
  c.add(g);
  const accent = opts.accent;
  if (accent !== null && accent !== undefined) {
    // Left accent-rail: a rounded bar hugging the inner-left edge, the shared family signature.
    const rail = scene.add.graphics();
    const railW = 4;
    rail.fillStyle(accent, 0.95);
    rail.fillRoundedRect(-w / 2 + 1, -h / 2 + 6, railW, h - 12, railW / 2);
    c.add(rail);
  }
  return c;
}

// ──────────────────────── Stat tiles / rank ribbon ────────────────────────

export interface StatTile {
  value: string;
  label: string;
  color?: number;
}

/** True when a stat value string's leading number is exactly zero (e.g. "0", "×0", "0%"). */
function statValueIsZero(value: string): boolean {
  const m = value.match(/-?\d[\d,]*\.?\d*/);
  if (!m) return false;
  return parseFloat(m[0].replace(/,/g, '')) === 0;
}

/**
 * A row of N stat tiles (e.g. 92% ACCURACY · ×5 BEST COMBO · 3 THREADED) centered at x. Value-aware
 * color (S3 item 4): a tile's accent only fires when its value is > 0 — a zero value falls back to
 * neutral grey so a "×0 BEST COMBO" / "0 THREADED" / "0 DECOYS EATEN" never wears an emphatic accent
 * (the audit's false-positive red on a zeroed DECOYS tile). Non-numeric values keep the passed color.
 */
export function statTileRow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  tiles: StatTile[],
  opts: { height?: number; gap?: number } = {},
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const n = Math.max(1, tiles.length);
  const gap = opts.gap ?? 8;
  const h = opts.height ?? 58;
  const tw = (w - gap * (n - 1)) / n;
  const startX = -w / 2;
  tiles.forEach((tile, i) => {
    const cx = startX + i * (tw + gap) + tw / 2;
    const g = scene.add.graphics();
    g.fillStyle(PALETTE.surface2, 1);
    g.fillRoundedRect(cx - tw / 2, -h / 2, tw, h, 10);
    g.lineStyle(1, PALETTE.line, 1);
    g.strokeRoundedRect(cx - tw / 2, -h / 2, tw, h, 10);
    c.add(g);
    const valColor = statValueIsZero(tile.value) ? PALETTE.dim : (tile.color ?? PALETTE.text);
    const val = scene.add
      .text(cx, -h / 2 + h * 0.34, tile.value, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(20, 14)}px`,
        color: hex(valColor),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add(val);
    c.add(labelText(scene, cx, -h / 2 + h * 0.72, tile.label, { size: 10, origin: 0.5 }));
  });
  return c;
}

/** Gold→player gradient ribbon: crown + title (gold) / sub + a right-aligned +XP (correct). */
export function rankRibbon(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  opts: { iconName?: IconName; title: string; sub?: string; xp?: string; height?: number } = { title: '' },
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const h = opts.height ?? 58;
  const g = scene.add.graphics();
  // subtle gold→player horizontal wash
  const segs = 40;
  const segW = w / segs;
  for (let i = 0; i < segs; i++) {
    const f = i / (segs - 1);
    const col = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.IntegerToColor(PALETTE.gold),
      Phaser.Display.Color.IntegerToColor(PALETTE.player),
      100,
      Math.round(f * 100),
    );
    g.fillStyle(Phaser.Display.Color.GetColor(col.r, col.g, col.b), 0.16);
    g.fillRect(-w / 2 + i * segW, -h / 2, segW + 1, h);
  }
  g.lineStyle(1.4, PALETTE.gold, 0.9);
  g.strokeRoundedRect(-w / 2, -h / 2, w, h, 12);
  c.add(g);
  const padX = 16;
  let cx = -w / 2 + padX;
  if (opts.iconName) {
    c.add(icon(scene, opts.iconName, cx + 11, 0, 22, PALETTE.gold));
    cx += 30;
  }
  const title = scene.add
    .text(cx, -8, opts.title.toUpperCase(), {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(15, 12)}px`,
      color: hex(PALETTE.gold),
      fontStyle: 'bold',
    })
    .setOrigin(0, 0.5);
  c.add(title);
  if (opts.sub) {
    c.add(
      scene.add
        .text(cx, 11, opts.sub, { fontFamily: FONT.family, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.dim) })
        .setOrigin(0, 0.5),
    );
  }
  if (opts.xp) {
    c.add(
      scene.add
        .text(w / 2 - padX, 0, opts.xp, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(15, 12)}px`,
          color: hex(PALETTE.correct),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5),
    );
  }
  return c;
}

// ─────────────────────────── Segmented tabs ───────────────────────────────

/** Segmented control (leaderboard scope, custom-lab twist). Active = scan fill / ink. */
export function segmentedTabs(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  tabs: string[],
  onSelect: (index: number) => void,
  initial = 0,
  accent: number = PALETTE.scan,
): { container: Phaser.GameObjects.Container; setActive: (i: number) => void } {
  const c = scene.add.container(x, y);
  const h = 36;
  const pad = 4;
  const bg = scene.add.graphics();
  bg.fillStyle(PALETTE.tile, 1);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
  c.add(bg);
  const segW = (w - pad * 2) / tabs.length;
  const fills: Phaser.GameObjects.Graphics[] = [];
  const labels: Phaser.GameObjects.Text[] = [];
  let active = initial;
  const paint = (): void => {
    tabs.forEach((_, i) => {
      const cx = -w / 2 + pad + i * segW + segW / 2;
      fills[i].clear();
      if (i === active) {
        fills[i].fillStyle(accent, 1);
        fills[i].fillRoundedRect(cx - segW / 2 + 1, -h / 2 + pad, segW - 2, h - pad * 2, 6);
      }
      labels[i].setColor(hex(i === active ? PALETTE.ink : PALETTE.dim));
    });
  };
  tabs.forEach((label, i) => {
    const cx = -w / 2 + pad + i * segW + segW / 2;
    const f = scene.add.graphics();
    fills.push(f);
    c.add(f);
    const t = scene.add
      .text(cx, 0, label, { fontFamily: FONT.display, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.dim), fontStyle: '600' })
      .setOrigin(0.5);
    labels.push(t);
    c.add(t);
    const hit = scene.add.rectangle(cx, 0, segW, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    c.add(hit);
    hit.on('pointerup', () => {
      active = i;
      paint();
      audio().playSfx('uiTap');
      onSelect(i);
    });
  });
  paint();
  const setActive = (i: number): void => {
    active = i;
    paint();
  };
  return { container: c, setActive };
}

// ───────────────────────────── List row ───────────────────────────────────

/** A surface list row (leaderboard / skins / cook checklist / board-by-board). */
export function listRow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  opts: {
    rank?: string;
    rankColor?: number;
    iconName?: IconName;
    iconColor?: number;
    swatch?: number;
    label: string;
    labelColor?: number;
    sublabel?: string;
    value?: string;
    valueColor?: number;
    height?: number;
    fill?: number;
    border?: number;
  },
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const h = opts.height ?? 46;
  const g = scene.add.graphics();
  g.fillStyle(opts.fill ?? PALETTE.tile, 1);
  g.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
  g.lineStyle(1, opts.border ?? PALETTE.line, 1);
  g.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
  c.add(g);
  let lx = -w / 2 + 16;
  if (opts.rank !== undefined) {
    const rk = scene.add
      .text(lx, 0, opts.rank, { fontFamily: FONT.display, fontSize: `${scaledFont(16, 13)}px`, color: hex(opts.rankColor ?? PALETTE.dim), fontStyle: 'bold' })
      .setOrigin(0, 0.5);
    c.add(rk);
    lx += 30;
  }
  if (opts.iconName) {
    c.add(icon(scene, opts.iconName, lx + 9, 0, 20, opts.iconColor ?? PALETTE.dim));
    lx += 28;
  }
  if (opts.swatch !== undefined) {
    const sw = scene.add.circle(lx + 11, 0, 11, opts.swatch).setStrokeStyle(1.5, PALETTE.ink, 0.6);
    c.add(sw);
    lx += 32;
  }
  const labelY = opts.sublabel ? -7 : 0;
  c.add(
    scene.add
      .text(lx, labelY, opts.label, { fontFamily: FONT.family, fontSize: `${scaledFont(15, 12)}px`, color: hex(opts.labelColor ?? PALETTE.text), fontStyle: '600' })
      .setOrigin(0, 0.5),
  );
  if (opts.sublabel) {
    c.add(
      scene.add
        .text(lx, 10, opts.sublabel, { fontFamily: FONT.family, fontSize: `${scaledFont(11, 11)}px`, color: hex(PALETTE.dim) })
        .setOrigin(0, 0.5),
    );
  }
  if (opts.value !== undefined) {
    c.add(
      scene.add
        .text(w / 2 - 16, 0, opts.value, { fontFamily: FONT.display, fontSize: `${scaledFont(15, 12)}px`, color: hex(opts.valueColor ?? PALETTE.player), fontStyle: 'bold' })
        .setOrigin(1, 0.5),
    );
  }
  return c;
}

// ───────────────────────────── Hearts row ─────────────────────────────────

/** A row of life hearts (filled = hazard, spent = mute). set(lives) updates; pulse(i) flashes one. */
export function heartRow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  max = 3,
  opts: { size?: number; gap?: number } = {},
): { container: Phaser.GameObjects.Container; set: (lives: number) => void; hearts: Phaser.GameObjects.Graphics[] } {
  const c = scene.add.container(x, y);
  const s = opts.size ?? 16;
  const gap = opts.gap ?? 5;
  const hearts: Phaser.GameObjects.Graphics[] = [];
  for (let i = 0; i < max; i++) {
    const g = icon(scene, 'heart', i * (s + gap), 0, s, PALETTE.hazard);
    hearts.push(g);
    c.add(g);
  }
  const set = (lives: number): void => {
    hearts.forEach((g, i) => {
      g.clear();
      drawIcon(g, 'heart', s, i < lives ? PALETTE.hazard : PALETTE.mute);
    });
  };
  set(max);
  return { container: c, set, hearts };
}

// ───────────────────────────── Timer pill ─────────────────────────────────

// ───────────────────────────── Slider / stepper ───────────────────────────

/** Horizontal slider (scan→player fill, white knob). Tap/drag to set. set(frac) updates visuals. */
export function slider(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  frac: number,
  onChange: (frac: number) => void,
  opts: { barH?: number } = {},
): { container: Phaser.GameObjects.Container; set: (frac: number) => void } {
  const c = scene.add.container(x, y);
  const h = opts.barH ?? 7;
  const trackG = scene.add.graphics();
  const knob = scene.add.circle(0, 0, 9, PALETTE.white).setStrokeStyle(1.5, PALETTE.lineStrong, 0.5);
  let f = Math.max(0, Math.min(1, frac));
  const paint = (): void => {
    trackG.clear();
    trackG.fillStyle(PALETTE.tile, 1);
    trackG.fillRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    const fw = w * f;
    const segs = 20;
    for (let i = 0; i < segs && fw > 0; i++) {
      const t = i / (segs - 1);
      const col = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.IntegerToColor(PALETTE.scan),
        Phaser.Display.Color.IntegerToColor(PALETTE.player),
        100,
        Math.round(t * 100),
      );
      trackG.fillStyle(Phaser.Display.Color.GetColor(col.r, col.g, col.b), 1);
      trackG.fillRect(-w / 2 + (fw / segs) * i, -h / 2, fw / segs + 1, h);
    }
    knob.setX(-w / 2 + w * f);
  };
  paint();
  const hit = scene.add.rectangle(0, 0, w, Math.max(44, h * 3), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([trackG, knob, hit]);
  let enabled = true;
  const setFromPointer = (px: number): void => {
    if (!enabled) return;
    const worldX = c.getWorldTransformMatrix().tx;
    const local = px - worldX;
    f = Math.max(0, Math.min(1, (local + w / 2) / w));
    paint();
    onChange(f);
  };
  hit.on('pointerdown', (p: Phaser.Input.Pointer) => setFromPointer(p.x));
  hit.on('pointermove', (p: Phaser.Input.Pointer) => {
    if (p.isDown) setFromPointer(p.x);
  });
  const set = (nf: number): void => {
    f = Math.max(0, Math.min(1, nf));
    paint();
  };
  c.setData('setEnabled', (on: boolean): void => {
    enabled = on;
    if (on) hit.setInteractive({ useHandCursor: true });
    else hit.disableInteractive();
  });
  return { container: c, set };
}

/** A −/value/+ stepper (text-size etc.). value text is `correct`. */
export function stepper(
  scene: Phaser.Scene,
  x: number,
  y: number,
  getLabel: () => string,
  onDec: () => void,
  onInc: () => void,
  opts: { boxSize?: number; gap?: number } = {},
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const s = opts.boxSize ?? 30;
  const gap = opts.gap ?? 14;
  const valTxt = scene.add
    .text(0, 0, getLabel(), { fontFamily: FONT.display, fontSize: `${scaledFont(15, 12)}px`, color: hex(PALETTE.correct), fontStyle: 'bold' })
    .setOrigin(0.5);
  const valW = 56;
  const mkBox = (bx: number, glyph: IconName, fn: () => void): void => {
    const g = scene.add.graphics();
    g.fillStyle(PALETTE.tile, 1);
    g.fillRoundedRect(bx - s / 2, -s / 2, s, s, 8);
    g.lineStyle(1, PALETTE.lineStrong, 1);
    g.strokeRoundedRect(bx - s / 2, -s / 2, s, s, 8);
    const gl = icon(scene, glyph, bx, 0, s * 0.5, PALETTE.text);
    const hit = scene.add.rectangle(bx, 0, Math.max(s, 44), Math.max(s, 44), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      audio().playSfx('uiTap');
      fn();
      valTxt.setText(getLabel());
    });
    c.add([g, gl, hit]);
  };
  // [−] [value] [+]
  mkBox(-valW / 2 - gap - s / 2, 'minus', onDec);
  c.add(valTxt);
  mkBox(valW / 2 + gap + s / 2, 'plus', onInc);
  return c;
}

function fmtClock(secs: number): string {
  const m = Math.floor(Math.max(0, secs) / 60);
  const s = Math.max(0, Math.floor(secs % 60));
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Clock icon + gradient pill (armed→correct, solid hazard <15%) + tabular m:ss. set(frac,secs). */
export function timerPill(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  opts: { barH?: number } = {},
): { container: Phaser.GameObjects.Container; set: (frac: number, secs: number) => void } {
  const c = scene.add.container(x, y);
  const barH = opts.barH ?? 8;
  const iconW = 26;
  const timeW = Math.min(112, Math.max(72, w - iconW - barH * 3));
  const barX = -w / 2 + iconW;
  const barW = Math.max(barH, w - iconW - timeW);
  const ic = icon(scene, 'clock', -w / 2 + 12, 0, 20, PALETTE.armed);
  const trackG = scene.add.graphics();
  const fillG = scene.add.graphics();
  const timeTxt = scene.add
    .text(w / 2, 0, '0:00', { fontFamily: FONT.display, fontSize: `${scaledFont(32, 26)}px`, color: hex(PALETTE.armed), fontStyle: 'bold' })
    .setOrigin(1, 0.5);
  c.add([ic, trackG, fillG, timeTxt]);
  const set = (frac: number, secs: number): void => {
    const cl = Math.max(0, Math.min(1, frac));
    trackG.clear();
    trackG.fillStyle(PALETTE.tile, 1);
    trackG.fillRoundedRect(barX, -barH / 2, barW, barH, barH / 2);
    fillG.clear();
    const low = cl < 0.15;
    if (low) {
      fillG.fillStyle(PALETTE.hazard, 1);
      if (cl > 0) fillG.fillRoundedRect(barX, -barH / 2, Math.max(barH, barW * cl), barH, barH / 2);
    } else {
      // armed→correct gradient across the filled width
      const fw = Math.max(barH, barW * cl);
      const segs = 16;
      for (let i = 0; i < segs; i++) {
        const f = i / (segs - 1);
        const col = Phaser.Display.Color.Interpolate.ColorWithColor(
          Phaser.Display.Color.IntegerToColor(PALETTE.armed),
          Phaser.Display.Color.IntegerToColor(PALETTE.correct),
          100,
          Math.round(f * 100),
        );
        fillG.fillStyle(Phaser.Display.Color.GetColor(col.r, col.g, col.b), 1);
        fillG.fillRect(barX + (fw / segs) * i, -barH / 2, fw / segs + 1, barH);
      }
    }
    ic.clear();
    drawIcon(ic, 'clock', 20, low ? PALETTE.hazard : PALETTE.armed);
    timeTxt
      .setText(fmtClock(secs))
      .setColor(hex(low ? PALETTE.white : PALETTE.armed))
      .setShadow(0, 0, hex(low ? PALETTE.hazard : PALETTE.void), low ? 8 : 0, false, true);
  };
  set(1, 0);
  return { container: c, set };
}
